import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Play, Pause, SkipForward, SkipBack, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface AudioSegment {
  id: number;
  speaker: string;
  voice: string;
  text: string;
  durationMs: number;
  audioBase64: string | null;
  status: 'success' | 'error';
}

interface SegmentedAudioResponse {
  segments: AudioSegment[];
  metadata: {
    totalSegments: number;
    successfulSegments: number;
    failedSegments: number;
    playbackSpeed: number;
    generatedAt: string;
  };
  errors: string[];
}

interface SegmentedAudioPlayerProps {
  storyId: number;
  storyContent: string;
  hasVoiceAssignments: boolean;
}

export function SegmentedAudioPlayer({ storyId, storyContent, hasVoiceAssignments }: SegmentedAudioPlayerProps) {
  const { toast } = useToast();
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(0.9);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioData, setAudioData] = useState<SegmentedAudioResponse | null>(null);

  // Generate segmented audio mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', `/api/stories/${storyId}/segmented-audio`, {
        playbackSpeed
      });
      return response.json();
    },
    onSuccess: (data: SegmentedAudioResponse) => {
      setAudioData(data);
      if (data.errors && data.errors.length > 0) {
        toast({
          title: "Some segments failed",
          description: data.errors.join(', '),
          variant: "destructive"
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Audio generation failed",
        description: error.message || "Failed to generate segmented audio",
        variant: "destructive"
      });
    }
  });

  // Load audio segment into audio element
  useEffect(() => {
    if (!audioData || !audioData.segments[currentSegmentIndex]) return;

    const segment = audioData.segments[currentSegmentIndex];
    
    if (segment.status === 'error' || !segment.audioBase64) {
      // Skip failed segments
      if (currentSegmentIndex < audioData.segments.length - 1) {
        setCurrentSegmentIndex(prev => prev + 1);
      } else {
        setIsPlaying(false);
      }
      return;
    }

    // Convert base64 to blob and create audio URL
    const byteCharacters = atob(segment.audioBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);

    // Create new audio element
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const audio = new Audio(url);
    audio.playbackRate = playbackSpeed;
    audioRef.current = audio;

    // Auto-play if playing
    if (isPlaying) {
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      });
    }

    // Handle segment end
    audio.onended = () => {
      if (currentSegmentIndex < audioData.segments.length - 1) {
        setCurrentSegmentIndex(prev => prev + 1);
      } else {
        setIsPlaying(false);
        setCurrentSegmentIndex(0);
      }
    };

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(url);
      }
    };
  }, [audioData, currentSegmentIndex, isPlaying, playbackSpeed]);

  const handlePlayPause = () => {
    if (!audioData) {
      // Generate audio first
      generateMutation.mutate();
      setIsPlaying(true);
      return;
    }

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
        });
      }
    }
    setIsPlaying(!isPlaying);
  };

  const handlePrevious = () => {
    if (currentSegmentIndex > 0) {
      setCurrentSegmentIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (audioData && currentSegmentIndex < audioData.segments.length - 1) {
      setCurrentSegmentIndex(prev => prev + 1);
    }
  };

  const handleRegenerate = () => {
    setAudioData(null);
    setCurrentSegmentIndex(0);
    setIsPlaying(false);
    generateMutation.mutate();
  };

  if (!hasVoiceAssignments) {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg" data-testid="container-no-voices">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          Assign voices to characters first to enable multi-voice playback
        </p>
      </div>
    );
  }

  const currentSegment = audioData?.segments[currentSegmentIndex];

  return (
    <div className="space-y-4" data-testid="container-segmented-player">
      {/* Current Segment Display */}
      {currentSegment && (
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Volume2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
              {currentSegment.speaker === 'narrator' ? 'Narrator' : currentSegment.speaker}
            </span>
            <span className="text-xs text-purple-600 dark:text-purple-400">
              ({currentSegment.voice})
            </span>
          </div>
          <p className="text-sm text-purple-800 dark:text-purple-200 italic">
            "{currentSegment.text.substring(0, 150)}{currentSegment.text.length > 150 ? '...' : ''}"
          </p>
        </div>
      )}

      {/* Player Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevious}
            disabled={currentSegmentIndex === 0 || !audioData}
            data-testid="button-previous"
          >
            <SkipBack className="w-4 h-4" />
          </Button>

          <Button
            variant="default"
            size="icon"
            onClick={handlePlayPause}
            disabled={generateMutation.isPending}
            data-testid="button-play-pause"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>

          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            disabled={!audioData || currentSegmentIndex === audioData.segments.length - 1}
            data-testid="button-next"
          >
            <SkipForward className="w-4 h-4" />
          </Button>
        </div>

        {audioData && (
          <div className="text-sm text-secondary">
            Segment {currentSegmentIndex + 1} of {audioData.metadata.totalSegments}
          </div>
        )}
      </div>

      {/* Speed Control */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Playback Speed</span>
          <span className="text-sm text-secondary">{playbackSpeed}x</span>
        </div>
        <Slider
          value={[playbackSpeed]}
          onValueChange={([value]) => setPlaybackSpeed(value)}
          min={0.5}
          max={1.5}
          step={0.1}
          className="w-full"
          disabled={isPlaying}
          data-testid="slider-speed"
        />
      </div>

      {/* Regenerate Button */}
      {audioData && (
        <Button
          variant="outline"
          onClick={handleRegenerate}
          disabled={generateMutation.isPending}
          className="w-full"
          data-testid="button-regenerate"
        >
          Regenerate Audio
        </Button>
      )}

      {/* Loading State */}
      {generateMutation.isPending && (
        <div className="text-center py-4" data-testid="text-generating">
          <p className="text-sm text-secondary">Generating multi-voice audio...</p>
        </div>
      )}
    </div>
  );
}
